import { useForm, Controller, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "@commons/Modal";
import TextInput from "@commons/Input/Text";
import { usePage } from "@hooks/usePage";
import SelectInput from "@commons/Input/Select";
import { GENDER_OPTIONS } from "@typings/entities/individuals";
import {
  REGISTER_ENVIRONMENT_OPTIONS,
  REGISTER_TYPE_OPTIONS,
} from "@typings/entities/registers/enums";
import { validateCoordinates } from "@utils/formatters";
import ComboBox from "@commons/Input/ComboBox";
import { searchTaxonomic } from "@services/taxonomicGroups/search";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import Map from "@commons/Map";
import PublicIcon from "@mui/icons-material/Public";
import Text from "@commons/Typography/Text";
import { createRegister } from "@services/registers/create";
import { ComboBoxDefault } from "@typings/components/comboBox";
import { useModalOpenWithCoordinates } from "@hooks/useModalOpenWithCoordinates";
import { Checkbox, FormControlLabel } from "@mui/material";
import { searchIndividuals } from "@services/individuals/search";
import { searchUsers } from "@services/users/search";
import { useEffect } from "react";
import { createAttachmentsRegister } from "@services/registers/createAttachments";
import { config } from "@/config";
import UploadInput from "@commons/Input/Upload";
import { globalMutate } from "@utils/swr";
import {
  registerSchema,
  type RegisterFormData,
} from "../../schemas/registerSchema";

function ModalNewRegister() {
  const { isModalOpen, closeModal, setIsLoading, setDataModal } = usePage();
  const open = isModalOpen("newRegister");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema) as Resolver<RegisterFormData>,
    defaultValues: {
      reporter: ComboBoxDefault,
      individual: ComboBoxDefault,
      anonymousIndividualTaxonomicGroup: ComboBoxDefault,
      anonymousIndividualGender: undefined,
      anonymousGroupMembersNumber: undefined,
      latitude: undefined,
      longitude: undefined,
      type: undefined,
      registerDate: undefined,
      environment: undefined,
      observations: "",
      sampleCollected: false,
    },
  });

  useModalOpenWithCoordinates<RegisterFormData>({ open, setValue });

  const individual = watch("individual");
  const anonymousTaxonomicGroup = watch("anonymousIndividualTaxonomicGroup");
  const anonymousGender = watch("anonymousIndividualGender");
  const anonymousGroupMembers = watch("anonymousGroupMembersNumber");

  const hasIndividual = !!individual?.value;
  const hasAnonymous = !!(
    anonymousTaxonomicGroup?.value ||
    anonymousGender ||
    anonymousGroupMembers
  );

  const isAnonymousDisabled = hasIndividual;
  const isIndividualDisabled = hasAnonymous;

  useEffect(() => {
    if (isAnonymousDisabled) {
      setValue("anonymousIndividualTaxonomicGroup", ComboBoxDefault);
      setValue("anonymousIndividualGender", undefined);
      setValue("anonymousGroupMembersNumber", undefined);
    }
  }, [isAnonymousDisabled, setValue]);

  useEffect(() => {
    if (isIndividualDisabled) {
      setValue("individual", ComboBoxDefault);
    }
  }, [isIndividualDisabled, setValue]);

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setIsLoading(true);

      const registerCreated = await createRegister({
        reporterId: data.reporter.value,
        individualId: data.individual?.value || undefined,
        anonymousIndividualTaxonomicGroupId:
          data.anonymousIndividualTaxonomicGroup?.value || undefined,
        anonymousIndividualGender: data.anonymousIndividualGender || undefined,
        anonymousGroupMembersNumber:
          Number(data.anonymousGroupMembersNumber) || undefined,
        observations: data.observations,
        type: data.type,
        registerDate: data.registerDate,
        environment: data.environment || undefined,
        sampleCollected: data.sampleCollected,
        latitude: Number(data.latitude),
        longitude: Number(data.longitude),
      });

      if (data.attachments && data.attachments.length > 0) {
        await createAttachmentsRegister({
          id: registerCreated.registerId,
          files: data.attachments || [],
        });
      }

      await globalMutate([`fetch-registers-geojson`, `fetch-get-registers`]);

      toast.success("Registro criado com sucesso");

      handleClose();
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError ? error.message : "Erro ao criar registro";
      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setDataModal({});
    closeModal();
  };

  if (!open) return null;

  const latitude = watch("latitude");
  const longitude = watch("longitude");

  const { hasValidCoordinates, lat, lng } = validateCoordinates(
    latitude,
    longitude,
  );

  return (
    <Modal
      open={open}
      heading="Cadastrar Registro"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-4xl"
    >
      <form className="grid grid-cols-1 lg:grid-cols-2 gap-4 gap-x-6">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="latitude"
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Latitude"
                  type="number"
                  textSize="sm"
                  fullWidth
                  error={!!errors.latitude}
                  helperText={errors.latitude?.message}
                />
              )}
            />
            <Controller
              name="longitude"
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Longitude"
                  type="number"
                  fullWidth
                  error={!!errors.longitude}
                  helperText={errors.longitude?.message}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <SelectInput
                  {...field}
                  label="Tipo de Registro"
                  options={REGISTER_TYPE_OPTIONS}
                  size="small"
                  fullWidth
                  value={field.value || ""}
                  error={!!errors.type}
                  helperText={errors.type?.message}
                />
              )}
            />

            <Controller
              name="registerDate"
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Data e hora"
                  type="datetime-local"
                  fullWidth
                  error={!!errors.registerDate}
                  helperText={errors.registerDate?.message}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Controller
              name="reporter"
              control={control}
              render={({ field }) => (
                <ComboBox
                  {...field}
                  label="Reportado por:"
                  value={field.value ?? null}
                  onChange={(option) => field.onChange(option ?? null)}
                  searchService={searchUsers}
                  error={!!errors.reporter}
                  helperText={errors.reporter?.label?.message}
                />
              )}
            />
            <div className="flex items-end">
              <Controller
                name="sampleCollected"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="success"
                      />
                    }
                    label="Amostra Coletada"
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className="h-64 lg:h-full w-full order-first lg:order-none">
          {hasValidCoordinates ? (
            <Map.Root>
              <Map.Localization latitude={lat} longitude={lng} draw />
            </Map.Root>
          ) : (
            <div className="flex flex-col items-center justify-center h-full bg-slate-100 rounded-md shadow-sm gap-2 p-6 text-center">
              <PublicIcon className="text-4xl text-gray-400" />
              <Text size="sm">
                Insira coordenadas válidas para visualizar o mapa.
              </Text>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:col-span-2">
          <div
            className={
              isIndividualDisabled ? "opacity-50 pointer-events-none" : ""
            }
          >
            <Controller
              name="individual"
              control={control}
              render={({ field }) => (
                <ComboBox
                  {...field}
                  label="Indivíduo"
                  value={field.value ?? null}
                  onChange={(option) => field.onChange(option ?? null)}
                  searchService={searchIndividuals}
                  error={!!errors.individual}
                  helperText={
                    errors.individual?.label?.message ||
                    (isIndividualDisabled
                      ? "Desabilitado quando outros dados estão preenchidos"
                      : "")
                  }
                  disabled={isIndividualDisabled}
                />
              )}
            />
          </div>

          <div
            className={
              isAnonymousDisabled ? "opacity-50 pointer-events-none" : ""
            }
          >
            <Controller
              name="anonymousIndividualTaxonomicGroup"
              control={control}
              render={({ field }) => (
                <ComboBox
                  {...field}
                  label="Espécie"
                  value={field.value ?? null}
                  onChange={(option) => field.onChange(option ?? null)}
                  searchService={searchTaxonomic}
                  error={!!errors.anonymousIndividualTaxonomicGroup}
                  helperText={
                    errors.anonymousIndividualTaxonomicGroup?.label?.message ||
                    (isAnonymousDisabled
                      ? "Desabilitado quando indivíduo está selecionado"
                      : "")
                  }
                  disabled={isAnonymousDisabled}
                />
              )}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:col-span-2">
          <div
            className={
              isAnonymousDisabled ? "opacity-50 pointer-events-none" : ""
            }
          >
            <Controller
              name="anonymousIndividualGender"
              control={control}
              render={({ field }) => (
                <SelectInput
                  {...field}
                  label="Sexo"
                  options={GENDER_OPTIONS}
                  size="small"
                  fullWidth
                  value={field.value || ""}
                  error={!!errors.anonymousIndividualGender}
                  helperText={
                    errors.anonymousIndividualGender?.message ||
                    (isAnonymousDisabled
                      ? "Desabilitado quando indivíduo está selecionado"
                      : "")
                  }
                  disabled={isAnonymousDisabled}
                />
              )}
            />
          </div>

          <div
            className={
              isAnonymousDisabled ? "opacity-50 pointer-events-none" : ""
            }
          >
            <Controller
              name="anonymousGroupMembersNumber"
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Quantidade de Animais"
                  fullWidth
                  type="number"
                  value={field.value || ""}
                  error={!!errors.anonymousGroupMembersNumber}
                  helperText={
                    errors.anonymousGroupMembersNumber?.message ||
                    (isAnonymousDisabled
                      ? "Desabilitado quando indivíduo está selecionado"
                      : "")
                  }
                  disabled={isAnonymousDisabled}
                />
              )}
            />
          </div>
        </div>

        <div className="lg:col-span-2">
          <Controller
            name="environment"
            control={control}
            render={({ field }) => (
              <SelectInput
                {...field}
                label="Ambiente"
                options={REGISTER_ENVIRONMENT_OPTIONS}
                size="small"
                fullWidth
                value={field.value || ""}
                error={!!errors.environment}
                helperText={errors.environment?.message}
              />
            )}
          />
        </div>

        <div className="lg:col-span-2">
          <Controller
            name="observations"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Observações"
                textSize="sm"
                fullWidth
                multiline
                rows={3}
                error={!!errors.observations}
                helperText={errors.observations?.message}
              />
            )}
          />
        </div>

        <div className="md:col-span-2">
          <Controller
            name="attachments"
            control={control}
            render={({ field: { value, onChange, onBlur, name } }) => (
              <UploadInput
                value={value || []}
                onChange={onChange}
                onBlur={onBlur}
                name={name}
                multiple={config.uploadArchive.multiple}
                accept={config.uploadArchive.accept}
                maxFiles={config.uploadArchive.maxFiles}
              />
            )}
          />
          {errors.attachments && (
            <p className="text-red-500 text-sm mt-1">
              {errors.attachments.message}
            </p>
          )}
        </div>
      </form>
    </Modal>
  );
}

export default ModalNewRegister;
